import dayjs from "dayjs";
import { useEffect, useState } from "react";

import "./index.css";
import DateRange from "../DateRange";
import Tray from "../History";
import Header from "../Header";
import Settings from "../Settings";
import Paper from "../Paper";
import { usePrintButton } from "../Paper/share";
import { readLocal, writeLocal, setTick, setSetting, resetDoc, keyForDay } from "../../sync/doc";
import { useSync } from "../../sync/useSync";
import { merge } from "../../sync/merge";

function Main() {
  // One document, one shape. Every tick and every setting carries its own time,
  // which is what lets two devices merge instead of overwrite.
  const [doc, setDoc] = useState(() => readLocal());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Persist as an effect, so the state updater below stays pure.
  useEffect(() => {
    writeLocal(doc);
  }, [doc]);

  // Always build on the newest document, never on the one this render captured.
  // Eleven separate useStates could not clobber each other; one object can, and
  // once sync can apply a merge at any moment a tick and an incoming merge can
  // land in the same batch.
  const commit = (update) => setDoc((prev) => (typeof update === "function" ? update(prev) : update));

  // Re-merge against the newest local document: `merged` was computed from a
  // snapshot taken when the sync began, and the user may have ticked since.
  // merge is idempotent, so doing it twice costs nothing.
  const sync = useSync({ doc, onMerged: (merged) => commit((prev) => merge(prev, merged)) });
  const syncProps = process.env.REACT_APP_GOOGLE_CLIENT_ID
    ? {
        enabled: sync.enabled,
        status: sync.status,
        account: sync.account,
        reason: sync.reason,
        onConnect: sync.connect,
        onDisconnect: sync.disconnect,
        onSyncNow: sync.syncNow,
      }
    : null;

  const value = (key) => doc.settings[key].value;
  const dateRange = value("dateRange");
  const startDate = dayjs(dateRange.startDate);
  const numberOfDays = dateRange.numberOfDays;
  const slots = value("slots");
  const title = value("title");
  const showWeekdays = value("showWeekdays");
  const shouldPrintHeader = value("shouldPrintHeader");
  const repeatStep = value("repeatStep");
  const repeatUnit = value("repeatUnit");
  const dateFormat = value("dateFormat");
  const weekdayFormat = value("weekdayFormat");

  const onDateRangeUpdate = (values) => {
    if (!values) return;
    const [from, to] = values;
    const count = to.diff(from, "day") + 1; // include first and last day
    commit((prev) => setSetting(prev, "dateRange", { startDate: keyForDay(from), numberOfDays: count }));
  };

  const onToggleTick = (day, slotId, on) => commit((prev) => setTick(prev, day, slotId, on));

  const set = (key) => (v) => commit((prev) => setSetting(prev, key, v));
  const onSetSlots = set("slots");
  const onSetTitle = set("title");
  const onSetDateFormat = set("dateFormat");
  const onSetWeekdayFormat = set("weekdayFormat");
  const onSetShowWeekdays = set("showWeekdays");
  const onSetShouldPrintHeader = set("shouldPrintHeader");
  const onSetRepeatStep = set("repeatStep");
  const onSetRepeatUnit = set("repeatUnit");

  // Reset writes deletions rather than absences: an absent tick would simply be
  // re-downloaded from Drive on the next sync and the reset would appear to fail.
  const onReset = () => commit((prev) => resetDoc(prev));

  const paperProps = {
    startFrom: startDate,
    numberOfDays,
    repeatStep,
    repeatUnit,
    slots,
    ticks: doc.ticks,
    dateFormat,
    weekdayFormat,
    showWeekdays,
    title,
    shouldPrintHeader,
  };
  const printButton = usePrintButton(paperProps);

  return (
    <div className="App">
      <Header
        onOpenSettings={() => setSettingsOpen(true)}
        onPrint={printButton.onPrint}
        sharesPdf={printButton.sharesPdf}
      >
        <DateRange startDate={startDate} numberOfDays={numberOfDays} onSetDateRange={onDateRangeUpdate} />
      </Header>

      <main className="Hall screen-only">
        <Tray
          numberOfDays={numberOfDays}
          startFrom={startDate}
          ticks={doc.ticks}
          dateFormat={dateFormat}
          slots={slots}
          onToggleTick={onToggleTick}
          showWeekdays={showWeekdays}
          weekdayFormat={weekdayFormat}
          repeatStep={repeatStep}
          repeatUnit={repeatUnit}
          title={title}
        />
      </main>

      <p className="Notice screen-only" role="status" aria-live="polite" hidden={!printButton.notice}>
        {printButton.notice}
      </p>

      {/* The paper face: the only thing that prints */}
      <Paper {...paperProps} className="print-only" />

      <Settings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        slots={slots}
        ticks={doc.ticks}
        onSetSlots={onSetSlots}
        showWeekdays={showWeekdays}
        onSetShowWeekdays={onSetShowWeekdays}
        dateFormat={dateFormat}
        onSetDateFormat={onSetDateFormat}
        weekdayFormat={weekdayFormat}
        onSetWeekdayFormat={onSetWeekdayFormat}
        title={title}
        onSetTitle={onSetTitle}
        repeatStep={repeatStep}
        onSetRepeatStep={onSetRepeatStep}
        repeatUnit={repeatUnit}
        onSetRepeatUnit={onSetRepeatUnit}
        shouldPrintHeader={shouldPrintHeader}
        onSetShouldPrintHeader={onSetShouldPrintHeader}
        onReset={onReset}
        sync={syncProps}
      />
    </div>
  );
}

export default Main;
